// components/pages/CartPage.tsx
import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Alert,
  Grid,
  IconButton,
  Divider,
  Paper,
  Avatar,
  Chip,
} from '@mui/material';
import {
  ShoppingCart as CartIcon,
  ArrowForward as ArrowIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingBag as ShoppingBagIcon,
  LocalShipping as ShippingIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';

// Styled Components
const CartItemCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: 16,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
  },
}));

const ProductImage = styled('img')({
  width: 80,
  height: 80,
  objectFit: 'cover',
  borderRadius: 12,
});

const QuantityButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.contrastText,
  width: 36,
  height: 36,
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
}));

const SummaryCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #2e5a33 0%, #4a7c59 100%)',
  color: 'white',
  borderRadius: 16,
  boxShadow: '0 8px 24px rgba(46, 90, 51, 0.3)',
}));

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP'
  }).format(amount);
};

const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const { items, updateQuantity, removeFromCart, getTotalItems, getTotalPrice, clearCart } = useCart();
  const { user } = useAuth();

  const handleIncrement = (productId: number, currentQuantity: number) => {
    updateQuantity(productId, currentQuantity + 1);
  };

  const handleDecrement = (productId: number, currentQuantity: number) => {
    if (currentQuantity > 1) {
      updateQuantity(productId, currentQuantity - 1);
    }
  };

  const handleRemoveItem = (productId: number) => {
    removeFromCart(productId);
  };

  const handleCheckout = () => {
    if (user) {
      navigate('/checkout');
    } else {
      navigate('/login', { state: { from: '/checkout' } });
    }
  };

  // Si el carrito está vacío
  if (items.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box textAlign="center" mb={4}>
          <ShoppingBagIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom color="text.secondary">
            Tu carrito está vacío
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={4}>
            ¡Descubre nuestros productos y comienza a llenar tu carrito!
          </Typography>
          
          <Button 
            variant="contained" 
            size="large"
            onClick={() => navigate('/productos')}
            endIcon={<ArrowIcon />}
            sx={{ 
              borderRadius: 3,
              px: 4,
              py: 1.5,
              background: 'linear-gradient(135deg, #2e5a33 0%, #4a7c59 100%)',
            }}
          >
            Explorar Productos
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box textAlign="center" mb={4}>
        <CartIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Tu Carrito de Compras
        </Typography>
        <Chip 
          label={`${getTotalItems()} ${getTotalItems() === 1 ? 'producto' : 'productos'}`}
          color="primary" 
          variant="outlined" 
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
        {/* Lista de productos */}
        <Box sx={{ flex: { md: 2 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Productos en tu carrito
            </Typography>
            <Button 
              variant="text" 
              color="error"
              onClick={clearCart}
              size="small"
            >
              Vaciar carrito
            </Button>
          </Box>

          {items.map((item) => (
            <CartItemCard key={item.product.id} elevation={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Imagen del producto */}
                <Box sx={{ flexShrink: 0 }}>
                  <ProductImage 
                    src={item.product.imagen || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjZTBlMGUwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPj88L3RleHQ+PC9zdmc+'} 
                    alt={item.product.nombre}
                  />
                </Box>

                {/* Información del producto */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {item.product.nombre}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {item.product.descripcion}
                  </Typography>
                  <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
                    {formatCurrency(item.product.precio_venta)}
                  </Typography>
                </Box>

                {/* Controles de cantidad */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <QuantityButton 
                    size="small"
                    onClick={() => item.product.id && handleIncrement(item.product.id, item.quantity)}
                  >
                    <AddIcon fontSize="small" />
                  </QuantityButton>
                  
                  <Typography variant="h6" sx={{ fontWeight: 700, minWidth: 24, textAlign: 'center' }}>
                    {item.quantity}
                  </Typography>
                  
                  <QuantityButton 
                    size="small"
                    onClick={() => item.product.id && handleDecrement(item.product.id, item.quantity)}
                  >
                    <RemoveIcon fontSize="small" />
                  </QuantityButton>
                </Box>

                {/* Subtotal y eliminar */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, minWidth: 100 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {formatCurrency(item.product.precio_venta * item.quantity)}
                  </Typography>
                  <IconButton 
                    color="error" 
                    size="small"
                    onClick={() => item.product.id && handleRemoveItem(item.product.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </CartItemCard>
          ))}

          {/* Botón continuar comprando */}
          <Box mt={3}>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/productos')}
              startIcon={<ArrowIcon sx={{ transform: 'rotate(180deg)' }} />}
            >
              Continuar comprando
            </Button>
          </Box>
        </Box>

        {/* Resumen del pedido */}
        <Box sx={{ flex: { md: 1 }, minWidth: { md: 300 } }}>
          <SummaryCard>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                Resumen del Pedido
              </Typography>

              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Subtotal ({getTotalItems()} productos)</Typography>
                  <Typography>{formatCurrency(getTotalPrice())}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Envío</Typography>
                  <Typography color="success.light">GRATIS</Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.3)' }} />

              <Box display="flex" justifyContent="space-between" mb={3}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Total
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {formatCurrency(getTotalPrice())}
                </Typography>
              </Box>

              <Button 
                variant="contained"
                fullWidth
                size="large"
                onClick={handleCheckout}
                sx={{ 
                  bgcolor: 'white',
                  color: 'primary.main',
                  fontWeight: 700,
                  py: 1.5,
                  borderRadius: 2,
                  '&:hover': {
                    bgcolor: 'grey.100'
                  }
                }}
              >
                {user ? 'Proceder al Checkout' : 'Iniciar Sesión para Comprar'}
              </Button>

              {!user && (
                <Alert severity="info" sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.1)', border: 'none' }}>
                  <Typography variant="caption" color="white">
                    Inicia sesión para guardar tu carrito y proceder con la compra
                  </Typography>
                </Alert>
              )}

              <Box mt={3} display="flex" alignItems="center" gap={1}>
                <ShippingIcon fontSize="small" />
                <Typography variant="caption">
                  Envío gratis en compras sobre $30.000
                </Typography>
              </Box>
            </CardContent>
          </SummaryCard>
        </Box>
      </Box>
    </Container>
  );
};

export default CartPage;